<template>
    <div class="product-page">
        <div v-if="hasCollection" @click="goBack()" class="back-button">&lt; BACK</div>
        <div class="main-title-container">
            <div>
                <p class="main-title">{{curatedBoxTitle}}</p>
                <p class="main-subtitle">{{ description }}</p>
            </div>
            <div class="box-qty-container">
                <div class="input-container">
                    <div class="box-qty-input-container" :class="canAddMore ? '' : 'no-more'">
                        <button :disabled="selectedBoxQty < minQty" @click="incrementBoxQty(false)">-</button> 
                        <input @blur="changeBoxQty(null)" v-model="selectedBoxQty" type="number" min="1"> 
                        <button v-tooltip="{content: 'Not enough inventory', container:false}" @click="incrementBoxQty(true)">+</button> 
                    </div>
                    <p class="min-alert" v-if="minQty > 1">Minimum: {{ minQty }}</p>
                </div>
            </div>
        </div>
        <div class="curated-boxes-page">
            <div class="curated-boxes-images-container">
                <p class="main-title">Box preview pictured below: {{ currentPrice }}</p>
                <div class="item-box">
                    <img class="box-image" width="649" height="492" src="https://cdn.shopify.com/s/files/1/1363/8433/files/emptybox_1.png?v=1659452617">
                    <div class="addon-images">
                        <div class="addon-image" v-for="addonProduct in selectedAddonProducts" :key="addonProduct.id">
                            <AddonBoxProduct :product="addonProduct" :cartProducts="selectedAddonProducts" :boxQty="boxQty"/>
                        </div>
                    </div>
                </div>
                <p class="products-container-title">What's inside: Scroll to review your gift</p>
                <ul class="products-container">
                    <li v-for="addonProduct in selectedAddonProducts" :key="addonProduct.variants[0].id">
                        <div class="addon-container">
                            <div class="image-container">
                                <img class="main-image" :src="addonProduct | getProductImage" >
                            </div>
                            <div class="product-information">
                                <div>
                                    <p class="title">{{addonProduct.title}}</p>
                                    <p class="price">{{addonProduct.variants[0].price}}</p>
                                </div>
                                <div class="product-qty-container">
                                    <select @change="updateAddon(addonProduct)" v-model="addonProduct.quantity">
                                        <option v-for="qty in addonMaxQty(addonProduct)" :key="qty" :value="qty">{{qty}}</option>
                                    </select>
                                    <img @click="removeProduct(addonProduct)" class="delete-button" src="https://cdn.shopify.com/s/files/1/1363/8433/files/trash_05a3e1b5-b394-4f91-aaf3-599964c057cb.svg?v=1659632833">
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
                <div v-if="!isMobile" class="product-legend">
                    <p class="title">Ship exactly how you need. In checkout, you'll be able to:</p>
                    <p class="subcopy">
                        <img src="https://cdn.shopify.com/s/files/1/1363/8433/files/houses.svg?v=1695923939"/>
                        <span>
                            Bulk ship to one location, ship to many individual addresses, or a mix of both!
                        </span>
                    </p>
                    <p class="subcopy postcard">
                        <img src="https://cdn.shopify.com/s/files/1/1363/8433/files/mail_615fe479-4bae-43fe-973f-196e3ac36a33.svg?v=1695923940"/>
                        <span>
                            Ship with just an email address. (no physical address needed)
                        </span>
                    </p>
                    <p class="subcopy box-wing">
                        <img src="https://cdn.shopify.com/s/files/1/1363/8433/files/flybox.svg?v=1695923939"/>
                        <span>Select your shipping speed: rush or standard, international or domestic.</span>
                    </p>
                </div>
            </div>
            <div class="curated-boxes-main-container">
                <div class="addons-container">
                    <p class="title">Step 1: Add or Swap Items</p>
                    <p class="subtitle">Scroll to add additional products. Price will change</p>
                    <div class="tab-container">
                        <div  v-if="!isLoading" class="addon-products-container">
                            <div class="addon-container" v-for="(addonProduct,i) in filteredProducts" :key="i">
                                <AddonProduct 
                                    @changeboxqty="changeBoxQty" 
                                    :product="addonProduct" 
                                    :cartProducts="selectedAddonProducts" 
                                    :boxQty="boxQty"
                                    :maxCapacity="maxCapacity"
                                    :currentCapacity="currentCapacity"
                                />
                            </div>
                            <div class="spinner-container" v-if="filteredProducts.length < 1">
                                <div class="spinner"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="write-container" class="write-container" v-if="!noCard && !paidCard">
                    <div class="pick-card-container">
                        <div class="pick-card-header">
                            <p class="title">Step 2: Pick Your Card</p>
                            <div class="blank-card-container">
                                <input v-model="isNoCard" id="noCard" type="checkbox">
                                <label for="noCard">Skip your Card</label>
                            </div>
                        </div>
                        <p v-if="showCardAlert" class="red-alert">Please select a card</p>
                        <div v-if="!isNoCard && cardProducts.length > 0" class="cards-container">
                            <div class="card-container" @click="selectCard(card)" :class="selectedCard.id == card.id ? 'selected' : ''" v-for="card in sortedCards" :key="card.id">
                                <img v-if="selectedCard.id == card.id " class="green-check" src="https://cdn.shopify.com/s/files/1/1363/8433/files/check-circle.svg?v=1658433688"/>
                                <img class="main-image lazyload" width="125" height="125" :data-src="card | getProductImage"/>
                            </div>
                        </div>
                    </div>
                    <div v-if="!isNoCard" class="write-header">
                        <p class="title">Write Your Note</p>
                        <div class="blank-card-container">
                            <input v-model="isEmptyMessage" id="blankCard" type="checkbox">
                            <label for="blankCard">Leave the card blank</label>
                        </div>
                    </div>
                    <div v-if="!isNoCard && !isEmptyMessage" class="form-container">
                        <p class="purple-span">Type in your message below. A real human will hand write your message and include it in your gift.</p>
                        <div class="message-container">
                            <p v-if="showAlert" class="red-alert">Please type your message below</p>
                            <label for="message">Your Message:</label>
                            <textarea :disabled="isEmptyMessage" placeholder="Start typing here..." maxlength="200" v-model="message.text" cols="30" rows="3" id="message"></textarea>
                        </div>
                        <div class="from-container">
                            <p v-if="showAlert" class="red-alert">Please type your message below</p>
                            <label for="from">From:</label>
                            <input :disabled="isEmptyMessage" placeholder="Your Name" maxlength="200" v-model="message.from" id="from" type="text">
                            <p class="purple-span" :class="remainingCharacters < 0 ? 'red' : ''">Character Remaining {{remainingCharacters}}</p>
                        </div>
                    </div>
                </div>
                <div class="write-container" v-else-if="!noCard && paidCard">
                    <div class="pick-card-container">
                        <div class="paid-card-header">
                            <div class="selection-container">
                                <p class="title">Add a handwritten card for $6</p>
                                <div class="blank-card-container">
                                    <input v-model="selectedPaidCard" id="blankCard" type="checkbox">
                                    <label for="blankCard">I want a card</label>
                                </div>
                            </div>
                            <p class="subtitle">
                                Include a card with each gift. Our team will handwrite your message for you and ship along with the item you choose, directly to your recipient
                            </p>
                        </div>
                        <div v-if="selectedPaidCard && sortedCards.length > 0" class="cards-container">
                            <div class="card-container" @click="selectCard(card)" :class="selectedCard.id == card.id ? 'selected' : ''" v-for="card in sortedCards" :key="card.id">
                                <img v-if="selectedCard.id == card.id " class="green-check" src="https://cdn.shopify.com/s/files/1/1363/8433/files/check-circle.svg?v=1658433688"/>
                                <img class="main-image" :src="card | getProductImage"/>
                            </div>
                        </div>
                    </div>
                    <div v-if="selectedPaidCard" class="write-header">
                        <p class="title">Write Your Note</p>
                    </div>
                    <div v-if="selectedPaidCard" class="form-container">
                        <p class="purple-span">Type in your message below. A real human will hand write your message and include it in your gift.</p>
                        <div class="message-container">
                            <p v-if="showAlert" class="red-alert">Please type your message below</p>
                            <label for="message">Your Message:</label>
                            <textarea :disabled="!selectedPaidCard" placeholder="Start typing here..." maxlength="200" v-model="message.text" cols="30" rows="3" id="message"></textarea>
                        </div>
                        <div class="from-container">
                            <p v-if="showAlert" class="red-alert">Please type your message below</p>
                            <label for="from">From:</label>
                            <input :disabled="!selectedPaidCard" placeholder="Your Name" maxlength="200" v-model="message.from" id="from" type="text">
                            <p class="purple-span" :class="remainingCharacters < 0 ? 'red' : ''">Character Remaining {{remainingCharacters}}</p>
                        </div>
                    </div>
                </div>
                <div v-if="!isMobile" class="end-buttons-container">
                    <button @click="openValidations(false)" class="happybox-button yellow">{{addBtnTxt}}</button>
                    <p class="preorder-alert" v-if="isPreorder">Ships in time for mid December delivery</p>
                </div>
                <div v-if="noCard && recoProducts.length > 0" class="more-gifts">
                    <p class="main-title">More Gifts</p>
                    <div v-if="!isMobile" class="products-container">
                        <div class="product-container" v-for="product in recoProducts" @click="selectNewProduct(product)" :key="product.id">
                            <img :src="product | getProductImage">
                            <div class="curated-footer">
                                <div class="curated-desc">
                                    <p class="title">{{product.title}}</p>
                                    <p class="price">{{product | moneyFormat}}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <VueSlickCarousel v-else-if="recoProducts.length > 0" :infinite="true" :dots="true" :arrows="false" :slidesToShow="3" :slidesToScroll="1">
                        <div class="product-container" v-for="product in recoProducts" @click="selectNewProduct(product)" :key="product.id">
                            <img :src="product | getProductImage">
                            <div class="curated-footer">
                                <div class="curated-desc">
                                    <p class="title">{{product.title}}</p>
                                    <p class="price">{{product | moneyFormat}}</p>
                                </div>
                            </div>
                        </div>
                    </VueSlickCarousel>
                </div>
            </div>
        </div>
        <div v-if="isMobile" class="end-buttons-container">
            <button @click="openValidations(false)" class="happybox-button yellow">{{addBtnTxt}}</button>
            <p class="preorder-alert" v-if="isPreorder">Ships in time for mid December delivery</p>
        </div>
        <div v-if="isMobile" class="product-legend">
            <p class="title">Ship exactly how you need. In checkout, you'll be able to:</p>
            <p class="subcopy">
                <img src="https://cdn.shopify.com/s/files/1/1363/8433/files/house_1bdaf796-2917-4938-9706-c759acf6e720.svg?v=1700074183"/>
                <span>
                    Bulk ship to one location, ship to many individual addresses, or a mix of both!
                </span>
            </p>
            <p class="subcopy postcard">
                <img src="https://cdn.shopify.com/s/files/1/1363/8433/files/mail_2dc82d09-387b-4432-b70d-a8d03f7099d7.svg?v=1700074183"/>
                <span>
                    Ship with just an email address. (no physical address needed)
                </span>
            </p>
            <p class="subcopy box-wing">
                <img src="https://cdn.shopify.com/s/files/1/1363/8433/files/wings.svg?v=1700074183"/>
                <span>Select your shipping speed: rush or standard, international or domestic.</span>
            </p>
        </div>
        <div v-if="!noCard && recoProducts.length > 0" class="more-gifts">
            <p class="main-title">More Gifts</p>
            <div v-if="!isMobile" class="products-container">
                <div class="product-container" v-for="product in recoProducts" @click="selectNewProduct(product)" :key="product.id">
                    <img :src="product | getProductImage">
                    <div class="curated-footer">
                        <div class="curated-desc">
                            <p class="title">{{product.title}}</p>
                            <p class="price">{{product | moneyFormat}}</p>
                        </div>
                    </div>
                </div>
            </div>
            <VueSlickCarousel v-else-if="recoProducts.length > 0" :infinite="true" :dots="true" :arrows="false" :slidesToShow="2" :slidesToScroll="1">
                <div class="product-container" v-for="product in recoProducts" @click="selectNewProduct(product)" :key="product.id">
                    <img :src="product | getProductImage">
                    <div class="curated-footer">
                        <div class="curated-desc">
                            <p class="title">{{product.title}}</p>
                            <p class="price">{{product | moneyFormat}}</p>
                        </div>
                    </div>
                </div>
            </VueSlickCarousel>
        </div>
        <div class="more-categories">
            <p class="main-title"><span v-if="currentCategory != ''">Not interested in {{currentCategory}} </span> Here are more categories...</p>
            <div v-if="!isMobile" class="categories-container">
                <div class="category-container" @click="changeCategory(category.handle)" v-for="category in filteredCategories" :key="category.handle">
                    <img :src="category.src">
                    <p class="title">{{ category.name }}</p>
                </div>
            </div>
            <VueSlickCarousel v-else-if="filteredCategories.length > 0" :infinite="true" :dots="true" :arrows="false" :slidesToShow="4" :slidesToScroll="4">
                <div class="category-container" @click="changeCategory(category.handle)" v-for="category in filteredCategories" :key="category.handle">
                    <img :src="category.src">
                    <p class="title">{{ category.name }}</p>
                </div>
            </VueSlickCarousel>
        </div>
        <BoxSelectionModal 
            v-if="boxModal"
            @close="closeBoxModal"
            @addcart="addToCart"
            :boxQty="boxQty"
        />
        <div class="global-spinner-container" v-if="filteredProducts.length < 1">
            <p>amazing gifts are loading...</p>
            <div class="global-spinner"></div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from "axios";
import VueSlickCarousel from 'vue-slick-carousel'
import AddonProduct from './AddonProduct.vue'
import AddonBoxProduct from './AddonBoxProduct.vue'
import BoxSelectionModal from './BoxSelectionModal.vue';
import Swal from 'sweetalert2/dist/sweetalert2.js'

import 'vue-slick-carousel/dist/vue-slick-carousel.css'
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css'
require('dotenv').config();


export default {
    components: {
        VueSlickCarousel,
        AddonProduct,
        AddonBoxProduct,
        BoxSelectionModal
    },
    data(){
        return{
            selectedImage: null,
            didModify: false,
            selectedCard: {},
            isEmptyMessage: false,
            isNoCard: false,
            selectedPaidCard: false,
            showCardAlert: false,
            boxQty: 1,
            selectedBoxQty: 1,
            message: {
            text: "",
            from: ""
            },
            showAlert: false,
            productLimit: 10,
            isLoading: false,
            boxModal: false,
        };
    },
    watch: {
        addonProducts: {
              handler(val){
                  if(this.addonProducts.length > 0 && this.curatedBox && this.selectedAddonProducts.length < 1){
                    this.getDefaultProducts();
                  }
              },
              deep: true
          },
      },
    props:{
        curatedBox: Object
    },
    computed:{
        ...mapGetters([
            'collectionProducts',
            'cardProducts',
            'mainCollection',
            'categoriesList',
            'addonProducts',
            'selectedAddonProducts',
            'maxCapacity',
            'currentCapacity',
            'defaultProducts',
            'feeProduct',
            "selectedBox"
        ]),
        currentImage(){
            if (this.selectedImage){
                return this.selectedImage;
            }
            else if (this.curatedBox){
                return this.curatedBox.images[0].src;
            }
        },
        curatedBoxTitle(){
            return this.curatedBox.title;
        },
        curatedBoxImages(){
            var images = []
            if(Object.keys(this.curatedBox).length != 0){
            images = this.curatedBox.images;
            var variantImages = this.curatedBox.variants.filter(x => x.featured_image).map(x => x.featured_image.src.replace("https:",""));
            images = images.filter(function(image) {
                return !variantImages.includes(image);
            });
            }
            return images;
        },
        remainingCharacters(){
            var remainingCharacters = 200;
            remainingCharacters = remainingCharacters - (this.message.text.length + this.message.from.length)
            return remainingCharacters;
        },
        description(){
            if(this.curatedBox)
            return this.curatedBox.body_html;
            
            return '';
        },
        boxesOnCart(){
            let boxes = 0;
            if(this.cart && this.curatedBox && this.cart.items){
                var cartItem = this.cart.items.filter(x => x.product_id == this.curatedBox.id);
                if(cartItem){
                    boxes = cartItem.reduce((prev, next) => prev + next.quantity, 0);;
                }
            }

            return boxes
        },
        maxBoxes(){
            let boxQty = 0 - this.boxesOnCart;
            if(this.curatedBox){
                if(this.curatedBox.variants){
                    if(this.curatedBox.variants.length > 1)
                        boxQty += this.curatedBox.variants.map(x => x.inventory_quantity).reduce((prev, next) => prev + next, 0);
                    else {
                        if(this.curatedBox.variants[0].inventory_policy == "continue"){
                            boxQty = 99999;
                        }
                        else
                            boxQty += this.curatedBox.variants[0].inventory_quantity;
                    }
                }  
            }

            return boxQty
        },
        canAddMore(){
            return this.boxQty < this.maxBoxes;
        },
        isMobile() {
            return window.innerWidth < 768;
        },
        noCard(){
            if(this.curatedBox){
            if(this.curatedBox.tags)
                return this.curatedBox.tags.includes("no_card")
            }

            return false;
        },
        paidCard(){
            if(this.curatedBox){
            if(this.curatedBox.tags)
                return this.curatedBox.tags.includes("paid_card");
            }

            return false;
        },
        recoProducts(){
            return this.collectionProducts.filter(x => x.id != this.curatedBox.id).slice(0,5);
        },
        currentCategory(){
        if(this.mainCollection && this.mainCollection.handle && this.categoriesList.length > 0){
          let category = this.categoriesList.find(x => x.handle == this.mainCollection.handle);

          return category.name;
        }

        return ""
      },
      filteredCategories(){
        if(this.currentCategory != ""){
            return this.categoriesList.filter(x => x.name != this.currentCategory);
        }

        return this.categoriesList;
      },
      priceVariantId(){
        if(this.curatedBox)
            if(this.curatedBox.variants){
                let variantPrice = this.curatedBox.variants.find(x => x.price != 0);
                return variantPrice;
            }

        return null
      },
      minQty(){
        if(this.curatedBox){
            let tags = [];
            if(Array.isArray(this.curatedBox.tags)){
                tags = this.curatedBox.tags
            }
            else {
                tags = this.curatedBox.tags.split(", ")
            }
            let minqtyTag = tags.find(x => x.includes("QTYMIN_"))
            if(minqtyTag){
                return parseInt(minqtyTag.split("_")[1]);
            }
        }
        return 1
      },
      hasCollection(){
        return this.mainCollection && this.mainCollection.handle;
      },
      isPreorder(){
        let tags = [];
        if(Array.isArray(this.curatedBox.tags)){
            tags = this.curatedBox.tags
        }
        else {
            tags = this.curatedBox.tags.split(", ")
        }
        return tags.find(x => x.includes("preorder"));
      },
      addBtnTxt(){
        if(this.isPreorder){
            return "Preorder";
        }
        else {
            return "add to cart";
        }
      },
        sortedCards(){
            if(this.curatedBox && this.cardProducts){
                let tags = [];
                if(Array.isArray(this.curatedBox.tags)){
                    tags = this.curatedBox.tags
                }
                else {
                    tags = this.curatedBox.tags.split(", ")
                }

                return this.cardProducts;
            }

            return [];
        },
        filteredProducts(){
            return this.addonProducts.filter((x) => {
                let hasMinQty = x.tags.find(y => y.includes("Customizable"))
                if(hasMinQty){
                return false
                }
                else{
                return true
                }
            })
        },
        currentPrice(){
            if(this.selectedAddonProducts){
                var price = this.selectedAddonProducts.map(x => x.variants.length ? parseFloat(x.variants[0].price * x.quantity) : 0).reduce((prev, next) => prev + next, 0);
                if(this.cardProducts && this.selectedCard.id)
                    price += parseFloat(this.selectedCard.id ? this.selectedCard.variants[0].price : this.cardProducts[0].variants[0].price);
                price += this.currentFee;
                return `$${(price * this.boxQty).toFixed(2)}`;
            }
            
            return 0;
        },
        bundleProducts(){
            var bundleProducts = [];
            bundleProducts.push(this.selectedAddonProducts);
            if(this.cardProducts.length > 0 && this.selectedCard.id){
                bundleProducts.push(this.selectedCard.id ? this.selectedCard : this.cardProducts[0]);
            }
            if(this.feeProduct)
                bundleProducts.push(this.feeProduct);
            bundleProducts.push(this.selectedBox)

            return bundleProducts.flat();
        },
        currentFee(){
            if(this.feeProduct){
                return this.feeProduct.variants[0].price / 100;
            }
            return 0
        },
        description(){
            const regEscape = v => v.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
            var productsDesc = this.curatedBox.body_html.split(new RegExp(regEscape("What's Inside"), "ig"));
            productsDesc = productsDesc[0];
            if(!productsDesc){
                return;
            }
                
            productsDesc = productsDesc.replace( /(<([^>]+)>)/ig, '').replaceAll('&amp;','&').replaceAll(':','').replaceAll('?','').replaceAll(/\u00a0/g, " ");
            return productsDesc.trim();
        },
    },
    methods:{
        selectImage(image){
            this.selectedImage = image;
        },
        incrementBoxQty(increment){
            if(increment){
                this.changeBoxQty(this.boxQty + 1);
            }
            else {
                this.changeBoxQty(this.boxQty - 1);
            }
        },
        changeBoxQty(qty){
            //Validate if cart products can support box qty if not show alert
            if (typeof this.selectedBoxQty === 'string' || this.selectedBoxQty instanceof String){
                this.selectedBoxQty = parseInt(this.selectedBoxQty);
            }
            this.selectedBoxQty = qty ? qty : this.selectedBoxQty
            var canUpdate = this.selectedAddonProducts.filter((x) =>{
                let qtyMin = x.tags.find(x => x.includes("QTYMIN") && !x.includes("DEMAND"));
                if(qtyMin){
                    qtyMin = qtyMin.split("_");
                    qtyMin = qtyMin[qtyMin.length - 1];

                    if(parseInt(qtyMin) > this.selectedBoxQty){
                        return true
                    }
                }
                if((this.selectedBoxQty * (x.quantity)) > x.variants[0].inventory_quantity)
                    return true

                return false
            })

            if(canUpdate.length > 0){
                this.openBoxQtyConfirmation(canUpdate);
            }
            else {
                this.boxQty = this.selectedBoxQty;
            }
        },
        openBoxQtyConfirmation(productList){
            var productTitles = productList.map(x => x.title);
            const alertText = "The following products dont have enough inventory to match the number of boxes: " + productTitles.join(", ") + ". Do you want to remove them?";
            Swal.fire({
                title: 'Are you sure?',
                text: alertText,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete them!',
                cancelButtonText: 'No, cancel!',
                reverseButtons: true
                }).then((result) => {
                if (result.isConfirmed) {
                    for(let addon of productList){
                        this.removeProduct(addon);
                    }
                    this.boxQty = this.selectedBoxQty
                } else if ( result.dismiss === Swal.DismissReason.cancel )
                {
                    this.selectedBoxQty = this.boxQty;
                }
                else{
                    this.selectedBoxQty = this.boxQty;
                }
            })
        },
        openValidations(){
            if(!this.selectedCard.id && !this.isNoCard){
                this.showCardAlert = true;
                document.getElementById("write-container").scrollIntoView();
                return;
            }
            this.showCardAlert = false;
            if(!this.isEmptyMessage && this.message.text.length < 1 && !this.isNoCard){
                this.showAlert = true;
                document.getElementById("write-container").scrollIntoView();
                return;
            }
            if(this.remainingCharacters < 0){
                return;
            }
            this.showAlert = false;
            this.boxModal = true;
        },
        async addToCart(){
            var properties = {};
            this.boxModal = false;
            if(this.isNoCard){
                await this.skipCard();
                this.isEmptyMessage = true;
            }
            properties = this.getGiftshipProperties();
            properties["Card"] = this.selectedCard.id ? this.selectedCard.title : this.cardProducts[0].title;
            if(!this.isEmptyMessage){
                properties["Message"] = this.message.text;
                properties["From"] = this.message.from;
            }
            properties["LeaveCardBlank"] = this.isEmptyMessage;
            properties["_uniqueKey"] =  Date.now();
            properties["boxName"] = this.curatedBox.title;
            properties["Box"] = this.selectedBox.title.includes("Box") ? this.selectedBox.title : (this.selectedBox.title + ' Box');

            var imagesUrls = this.selectedAddonProducts.map(x =>  x.images[x.images.length - 1].src)
            properties["productImages"] = imagesUrls;
            properties["isCurated"] = true;
            if(this.minQty > 1){
                properties.qtyMin = this.minQty;
            }

            var items = "";
            this.selectedAddonProducts.forEach((product, i) => {
                var propertyId = "Item_" + (i + 1);
                var title = `${product.quantity}x ${product.title}`
                if(product.variants[0].title != "Default Title"){
                    title += ` - ${(product.variants[0].title)}`
                }
                properties[propertyId] = title;
                if((i + 1) == this.selectedAddonProducts.length){
                    items += ` ${title}`
                }else{
                    items += ` ${title},`
                }
            });
            properties["Items"] = items;
            this.ajaxAddCart({
                id: 41663054184613,
                quantity: this.boxQty,
                properties: properties,
            })
        },
        async getEmptyCard(){
            return await axios
            .get("/products/no-card.js")
            .catch((err) => {
                console.log(err);
            });
        },
        async skipCard(){
            const noCard = await this.getEmptyCard();
            this.selectedCard = noCard.data;
            this.message.from = "";
            this.message.text = "";

        },
        ajaxAddCart(item) {
            axios
            .post("/cart/add.js", item)
            .then((response) => {
                let hasCart = localStorage.getItem("cartDate");
                if(!hasCart){
                    let newDate = new Date();
                    let stringDate = newDate.getFullYear() + "-"+ (newDate.getMonth() + 1) + "-" + newDate.getDate();
                    localStorage.setItem("cartDate",stringDate);
                }
                localStorage.setItem('finishedBox',true);
                $('.cartClicker, #AddToCart').click();
            })
            .catch((err) => {
                console.log(err);
            });
        },
        goBack(){
            if(this.mainCollection && this.mainCollection.handle){
                history.pushState(null,"",`/collections/${this.mainCollection.handle}`);
                this.$store.commit("setSelectedAddonProducts",[]);
                this.$store.commit("setSelectedProduct",null);
            }
        },
        selectNewProduct(product){
            this.selectedCard = {};
            this.$store.commit("setSelectedProduct",product);
            this.getSingleProduct(product.handle);
            $(window).scrollTop(0);
        },
        selectCard(card){
            this.selectedCard = card;
        },
        changeCategory(handle){
            this.isLoading = true;
            this.$emit("changecategory", handle)
        },
        async getSingleProduct(handle){
            axios.get(`${handle}.json`)
            .then(response => response.data)
            .then((data) => {
                this.$store.commit("setSelectedProduct",data.product);
            })
        },
        getDefaultProducts(){
            var addonProducts = [];
            var productsDesc = this.curatedBox.body_html.toLowerCase().split("inside");
            productsDesc = productsDesc[1];
            if(!productsDesc){
                return;
            }
                
            productsDesc = productsDesc.replace( /(<([^>]+)>)/ig, '').replaceAll('&amp;','&').replaceAll(':','').replaceAll('?','').replaceAll(/\u00a0/g, " ").split("\n");
            let addonsDesc = productsDesc.filter(x => x.trim());

            //Get Default Products using Name
            for (var addonDesc of addonsDesc){
                addonDesc = addonDesc.trim();
                const qty = addonDesc.split(" ")[0];
                const trueQty = parseInt(qty) * this.minQty;
                var variantName = "";
                var productName = "";
                var totalProduct = addonDesc.slice(qty.length + 1,addonDesc.length).split(" - ");
                productName = totalProduct[0];
                if(totalProduct.length > 1){
                variantName = totalProduct[1];
                }
                
                var currentProduct = this.addonProducts.find(x => x.title.toLowerCase() == productName);
                if(currentProduct){
                    if(variantName != ""){
                        currentProduct.variants = currentProduct.variants.filter(x => x.title.toLowerCase() == variantName);
                    }
                    if(currentProduct.variants[0].inventory_quantity >= trueQty){
                        currentProduct.quantity = parseInt(qty);
                        addonProducts.push(currentProduct);
                    }
                }
            }
            this.$store.commit("setDefaultProducts",JSON.parse(JSON.stringify(addonProducts)));
            this.$store.commit("setSelectedAddonProducts",JSON.parse(JSON.stringify(addonProducts)));
        },
        removeProduct(product){
            this.$store.commit('removeSelectedAddonProductNew', product);
        },
        updateAddon(event){
            this.$store.commit('updateSelectedAddonProductsNew',{product: event, quantity: event.quantity});
        },
        addonMaxQty(addon){
            let tMax = this.getProductMaxQty(addon)
            const min = 1,
            max = tMax < 1 ? 1 : tMax,
            interval = 1;
            const length = (max - min) / interval + 1;
            const arr = Array.from({ length }, (_, i) => min + i * interval);

            return arr;
        },
        getProductMaxQty(addon){
            //Get max value posible in products, no more than the product limit per box
            var maxValues = [this.productLimit]
            //Get limit based on inventory * total boxes
            if(this.defaultProducts.find(x => x.id == addon.id)){
                let qty = Math.floor(addon.variants[0].inventory_quantity / this.boxQty);
                qty = qty < 1 ? 1 : qty;
                maxValues.push(qty);
            }
            else {
                maxValues.push(Math.floor(addon.variants[0].inventory_quantity / this.boxQty));
            }
            //Get limit based on currentCapacity
            let capacity = addon.capacity;
            capacity = capacity != null ? addon.capacity : 5
            maxValues.push(Math.floor(((this.maxCapacity - this.currentCapacity) / capacity) + this.getProductCurrentQty(addon)));
            return Math.min(...maxValues);
        },
        getProductCurrentQty(currentProduct){
            var product = this.selectedAddonProducts.find(x => x.id == currentProduct.id);
            if(product)
                return product.quantity
            return 0
        },
        getGiftshipProperties(){
            var products = this.bundleProducts;
            var titles = products.map((x) => {
                let isVariant = x.variants[0].title != "Default Title"
                if(isVariant)
                    return x.title + " - " + x.variants[0].title + " - Quantity: " + (x.quantity ? x.quantity : 1); 
                else 
                    return x.title +  " - Quantity: " + (x.quantity ? x.quantity : 1); 
            })
            var ids = products.map(x => x.variants[0].id);
            var prices = products.map((x) => {
                let currentPrice = 0;
                let quantity = x.quantity ? x.quantity : 1;
                if(typeof x.variants[0].price === 'string' || x.variants[0].price instanceof String)
                    currentPrice = parseInt(x.variants[0].price.replaceAll(".",""));
                else 
                currentPrice = x.variants[0].price;
                return currentPrice * quantity;
            });
            var quantities = products.map(x => x.quantity ? x.quantity : 1);
            var properties = {};
            properties["_gs_bundle_contents"] = titles;
            properties["_gs_bundle_ids"] = ids;
            properties["_gs_bundle_prices"] = prices;
            properties["_gs_bundle_quantities"] = quantities;
            properties["_gs_bundle_discount"] = 0;

            return properties;
        },
        closeBoxModal(){
            this.boxModal = false;
        }
    },
    beforeMount(){
    },
    mounted() {
        if(this.curatedBox){
            let tags = [];
            if(Array.isArray(this.curatedBox.tags)){
                tags = this.curatedBox.tags
            }
            else {
                tags = this.curatedBox.tags.split(", ")
            }
            let minqtyTag = tags.find(x => x.includes("QTYMIN_"))
            if(minqtyTag){
                this.boxQty = parseInt(minqtyTag.split("_")[1]);
                this.selectedBoxQty = parseInt(minqtyTag.split("_")[1]);
            }
            this.getSingleProduct(this.curatedBox.handle);
            if(this.addonProducts.length > 0 && this.curatedBox && this.selectedAddonProducts.length < 1)
                this.getDefaultProducts();
        }
    },
    destroy() {
    }
}
</script>

<style scoped>
.global-spinner-container{
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0,0,0,0.2);
  position: fixed;
  height: 100%;
  z-index: 999;
  top: 0;
}

.global-spinner-container p{
  font-size: 16px;
  color: white !important;
  font-weight: bold;
}

.global-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #09f;
  animation: spin 1s ease infinite;
}
.addon-products-container{
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
}

.addon-products-container .addon-container {
    width: 30%;
}

.tab-container{
    max-height: 25vw;
    overflow-y: scroll;
}

.item-box{
      width: 100%;
      position: relative;
      display: flex;
    }
  
    .item-box .box-image{
      width: 100%;
      height: auto;
    }
  
    .item-box .addon-images{
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-around;
      padding: 5%;
      overflow: hidden;
    }
  
    .item-box .addon-images .addon-image{
      width: 25%;
      max-width: 50%;
      flex: 0 1 auto;
    }
  
    .addon-image:first-child:nth-last-child(1) {
      width: auto;
      max-width: 60%;
      max-height: 100%;
    }
  
    .addon-image:first-child:nth-last-child(2),
    .addon-image:first-child:nth-last-child(2) ~ .addon-image {
      width: auto;
      max-width: 50%;
      max-height: 100%
    }
  
    .addon-image:first-child:nth-last-child(3),
    .addon-image:first-child:nth-last-child(3) ~ .addon-image {
      width: auto;
      height: 50%;
    }
  
    .addon-image:first-child:nth-last-child(4),
    .addon-image:first-child:nth-last-child(4) ~ .addon-image {
      width: auto;
      height: 50%;
    }
  
    .addon-image:first-child:nth-last-child(5),
    .addon-image:first-child:nth-last-child(5) ~ .addon-image {
      width: auto;
      max-width: 33%;
      max-height: 50%;
    }
  
    .addon-image:first-child:nth-last-child(6),
    .addon-image:first-child:nth-last-child(6) ~ .addon-image {
      width: auto;
      max-width: 33%;
      max-height: 50%;
    }

    .curated-boxes-images-container{
        padding-top: unset;
    }

    .addons-container{
        border-bottom: 1px solid #E8E1F2;
        padding-bottom: 5%;
    }

    .addons-container .title,.write-container .pick-card-header .title,.write-container .write-header .title{
        font-size: 1.8vw;
        font-family: karla;
        font-weight: bold;
        line-height: 1.2;
    }

    .addons-container .subtitle{
        font-size: 1.2vw;
        margin-bottom: 2%;
    }

    .main-title-container{
        display: flex;
        align-items: center;
        padding: 0 0% 1% 0%;
        margin: 2% 0% 2% 5%;
        gap: 15px;
        width: 47.5%;
        border-bottom: 1px solid #E8E1F2;
    }

    .main-title-container .main-title{
        margin: unset;
        white-space: nowrap;
        font-size: 4vw;
    }

    .write-container{
        padding-top: 90px;
        margin-top: -90px;
    }

    .main-title-container .box-qty-container{
        width: 50%;
    }

    .curated-boxes-images-container .main-title{
        font-size: 1.8vw;
        font-family: karla;
        font-weight: bold;
        margin: unset;
    }

    .box-qty-container .input-container{
        width: 100%;
    }

    .products-container-title{
        font-size: 1.8vw;
        font-family: karla;
        font-weight: bold;
        margin: unset;
        padding: 0 2%;
    }

    .main-title-container .box-qty-container{
        width: auto;
    }

    .main-title-container .box-qty-container .box-qty-input-container{
        width: 100% !important;
    }

    .main-title-container .box-qty-container .box-qty-input-container input{
        width: 25% !important;
        min-width: 25px;
    }

    @media screen and (max-width:767px){

        .main-title-container .box-qty-container .box-qty-input-container{
            width: 100%;
        }

        .curated-boxes-images-container .main-title{
            font-size: 3.5vw;
            text-align: left;
        }

        .products-container-title{
            font-size: 3.5vw;
            border-bottom: 1px solid #E8E1F2;
            margin-bottom: 3%;
        }

        .addons-container .title,.write-container .pick-card-header .title,.write-container .write-header .title{
            font-size: 5vw;
        }
        .addons-container .title{
            font-size: 5vw;
        }

        .addons-container .subtitle{
            font-size: 3vw;
            border-bottom: 1px solid #E8E1F2;
            padding-bottom: 2%;
            margin-bottom: 3%;
        }

        .curated-boxes-page{
            padding-bottom: 0;
        }

        .end-buttons-container{
            position: sticky;
            bottom: 0;
            padding: 2% 5%;
            width: 100%;
            background-color: white;
        }

        .end-buttons-container .happybox-button{
            width: 100%;
        }

        .tab-container{
            max-height: 40vh;
        }

        .addon-products-container .addon-container{
            width: 45%;
        }

        .addon-products-container{
            padding: 0% 5%;
        }

        .curated-boxes-main-container{
            padding-top: 10%;
        }

        .main-title-container{
            width: 100%;
            padding: 0 5%;
            margin: unset;
        }

        .main-title-container .main-title{
            font-size: 6vw;
        }

        .main-title-container .box-qty-container{
            margin: 3% 0;
        }

        .main-title-container{
            border-bottom: 1px solid #E8E1F2;
        }

        .product-page .products-container{
            box-shadow: 0 0 0 3px #fafafa;
            border-radius: 8px;
            background-color: #fafafa;
        }

        .item-box{
            margin-bottom: 5%;
        }

        .write-container{
            padding-top: calc(90px + 10%);
        }

        .write-container .write-header{
            padding-top: 5%;
        }
    }
</style>